import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { Ga4AnalyticsService } from '../../../core/analytics/ga4-analytics.service';
import { AuthStateService } from '../../../core/auth/auth-state.service';
import { NumeronCollectionModel, NumeronItemModel } from '../models/numeron.model';
import { NumeronsService } from '../services/numerons.service';

@Component({
  selector: 'app-delete-numeron-page',
  imports: [RouterLink],
  templateUrl: './delete-numeron-page.component.html',
})
export class DeleteNumeronPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly ga4AnalyticsService = inject(Ga4AnalyticsService);
  private readonly numeronsService = inject(NumeronsService);
  private readonly authStateService = inject(AuthStateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly collectionId = this.route.snapshot.paramMap.get('collectionId') ?? '';
  protected readonly cancelHref = this.resolveReturnHref('returnTo', '/mynumerons');
  private readonly successHref = this.resolveReturnHref('successReturnTo', this.cancelHref);
  private readonly routeItemTitle =
    this.route.snapshot.queryParamMap.get('itemTitle')?.trim() ?? '';
  protected readonly isLoading = signal(true);
  protected readonly isDeleting = signal(false);
  protected readonly loadErrorMessage = signal('');
  protected readonly deleteErrorMessage = signal('');
  protected readonly loadedCollection = signal<NumeronCollectionModel | null>(null);

  public constructor() {
    this.title.setTitle('Delete Item');
    this.ga4AnalyticsService.trackPageView({
      contentId: this.collectionId || undefined,
      contentType: 'Delete Item',
      contentGroup: 'My Numerons',
    });
    this.loadCollection();
  }

  protected canDelete(): boolean {
    return !this.isLoading() && this.loadErrorMessage() === '' && this.loadedCollection() !== null;
  }

  protected isCollectionDelete(): boolean {
    return this.loadedCollection()?.isCollection === true;
  }

  protected pageHeading(): string {
    return this.isCollectionDelete() ? 'Delete Collection' : 'Delete Numeron';
  }

  protected loadingMessage(): string {
    return this.isCollectionDelete()
      ? 'Loading collection details...'
      : 'Loading numeron details...';
  }

  protected itemTitle(): string {
    const collection = this.loadedCollection();
    if (collection?.isCollection) {
      return (
        collection.title?.trim() ||
        this.routeItemTitle ||
        collection.numerons[0]?.title?.trim() ||
        'this Collection'
      );
    }

    return collection?.numerons[0]?.title?.trim() || collection?.title?.trim() || 'this Numeron';
  }

  protected itemLabel(): string {
    return this.isCollectionDelete() ? 'Collection' : 'Numeron';
  }

  protected deleteButtonLabel(): string {
    if (this.isDeleting()) {
      return this.isCollectionDelete() ? 'Deleting...' : 'Deleting...';
    }

    return this.isCollectionDelete() ? 'Delete Collection' : 'Delete Numeron';
  }

  protected deleteNumeron(): void {
    if (!this.canDelete() || this.isDeleting()) {
      return;
    }

    this.isDeleting.set(true);
    this.deleteErrorMessage.set('');

    this.numeronsService
      .deleteMyNumeron(this.collectionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async () => {
          this.isDeleting.set(false);
          await this.router.navigateByUrl(this.successHref);
        },
        error: () => {
          this.isDeleting.set(false);
          this.deleteErrorMessage.set(
            this.isCollectionDelete()
              ? 'Oops, could not delete Collection!'
              : 'Oops, could not delete Numeron!',
          );
        },
      });
  }

  private resolveReturnHref(queryParam: string, fallback: string): string {
    const returnTo = this.route.snapshot.queryParamMap.get(queryParam)?.trim() ?? '';
    return returnTo.startsWith('/mynumerons') ? returnTo : fallback;
  }

  private loadCollection(): void {
    if (!this.collectionId) {
      this.loadErrorMessage.set('Numeron details are unavailable right now. Please try again.');
      this.isLoading.set(false);
      return;
    }

    this.numeronsService
      .getMyNumeronCollection(this.collectionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (collection) => {
          if (collection.isCollection) {
            if (!this.isCollectionPublisherMatch(collection)) {
              this.loadErrorMessage.set(
                'You can delete only Collections published by your account.',
              );
              this.isLoading.set(false);
              return;
            }

            this.loadedCollection.set(collection);
            this.title.setTitle(collection.title?.trim() || 'Delete Collection');
            this.isLoading.set(false);
            return;
          }

          const numeron = collection.numerons[0];
          if (!numeron) {
            this.loadErrorMessage.set(
              'Numeron details are unavailable right now. Please try again.',
            );
            this.isLoading.set(false);
            return;
          }

          if (!this.isNumeronPublisherMatch(collection, numeron)) {
            this.loadErrorMessage.set('You can delete only Numerons published by your account.');
            this.isLoading.set(false);
            return;
          }

          this.loadedCollection.set(collection);
          this.title.setTitle(collection.title?.trim() || 'Delete Numeron');
          this.isLoading.set(false);
        },
        error: () => {
          this.loadErrorMessage.set(
            'Collection or Numeron details are unavailable right now. Please try again.',
          );
          this.isLoading.set(false);
        },
      });
  }

  private isCollectionPublisherMatch(collection: NumeronCollectionModel): boolean {
    const authenticatedPublisherId = this.authStateService.authUser()?.backendUserId?.trim() ?? '';
    const authenticatedDisplayName = this.authStateService.authUser()?.displayName?.trim() ?? '';
    const collectionPublisherId = collection.user?._id?.trim() ?? '';
    const collectionPublisherName = collection.user?.displayName?.trim() ?? '';

    if (authenticatedPublisherId) {
      return authenticatedPublisherId === collectionPublisherId;
    }

    if (authenticatedDisplayName) {
      return authenticatedDisplayName === collectionPublisherName;
    }

    return true;
  }

  private isNumeronPublisherMatch(
    collection: NumeronCollectionModel,
    numeron: NumeronItemModel,
  ): boolean {
    const authenticatedPublisherId = this.authStateService.authUser()?.backendUserId?.trim() ?? '';
    const authenticatedDisplayName = this.authStateService.authUser()?.displayName?.trim() ?? '';
    const collectionPublisherId = collection.user?._id?.trim() ?? '';
    const numeronPublisherId = numeron.user?._id?.trim() ?? '';
    const collectionPublisherName = collection.user?.displayName?.trim() ?? '';
    const numeronPublisherName = numeron.user?.displayName?.trim() ?? '';

    if (authenticatedPublisherId) {
      return (
        authenticatedPublisherId === collectionPublisherId ||
        authenticatedPublisherId === numeronPublisherId
      );
    }

    if (authenticatedDisplayName) {
      return (
        authenticatedDisplayName === collectionPublisherName ||
        authenticatedDisplayName === numeronPublisherName
      );
    }

    return true;
  }
}
